/**
 * Owner Console unlock token, kept for this browser tab only (sessionStorage,
 * so it is gone when the tab closes). The API wants it in X-Owner-Token on
 * every console route (backend requireOwnerUnlock); the portal JWT alone gets
 * 401 { code: 'OWNER_LOCKED' }.
 */
const TOKEN_KEY = 'ownerToken';
const EXPIRES_KEY = 'ownerTokenExpiresAt';

const store = () => {
  try { return window.sessionStorage; } catch (e) { return null; }
};

export const getOwnerToken = () => {
  const s = store();
  if (!s) return null;
  const token = s.getItem(TOKEN_KEY);
  const expiresAt = Date.parse(s.getItem(EXPIRES_KEY) || '');
  if (!token || !expiresAt || expiresAt <= Date.now()) return null;
  return token;
};

export const getOwnerExpiresAt = () => {
  const s = store();
  const at = s ? Date.parse(s.getItem(EXPIRES_KEY) || '') : NaN;
  return Number.isFinite(at) && at > Date.now() ? new Date(at) : null;
};

export const setOwnerUnlock = (token, expiresAt) => {
  const s = store();
  if (!s) return;
  s.setItem(TOKEN_KEY, token);
  s.setItem(EXPIRES_KEY, new Date(expiresAt).toISOString());
};

export const clearOwnerUnlock = () => {
  const s = store();
  if (!s) return;
  s.removeItem(TOKEN_KEY);
  s.removeItem(EXPIRES_KEY);
};

/** Header to merge into console requests; empty while locked. */
export const ownerHeader = () => {
  const token = getOwnerToken();
  return token ? { 'X-Owner-Token': token } : {};
};

/**
 * The API refused the unlock: either there is none / it expired (401 OWNER_LOCKED)
 * or the stored one was issued to a different user (403), e.g. after logging in
 * as someone else in the same tab. Either way the stored token is useless, so it
 * is cleared here and the caller shows the unlock dialog again.
 */
export const isOwnerLockedError = (error) => {
  const status = error?.response?.status;
  const locked = status === 401 && error.response.data?.code === 'OWNER_LOCKED';
  const foreign = status === 403 && /unlock belongs to another user/i.test(error.response.data?.message || '');
  if (foreign) clearOwnerUnlock();
  return locked || foreign;
};
